// @mui
import Box, { BoxProps } from '@mui/material/Box';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import { useSettingsContext } from 'src/components/settings';
//
import { HEADER, NAV } from '../config-layout';

// ----------------------------------------------------------------------

const SPACING = 8;

export default function Main({ children, sx, ...other }: BoxProps) {
  const settings = useSettingsContext();

  const lgUp = useResponsive('up', 'lg');

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  // Detect if Header renders the sub-tabs bar (see header.tsx + `header-tabs.tsx`)
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const subBarRoots = [
    '/optimization/overview',
    '/optimization/conversions-analysis',
    '/optimization/funnel',
    '/optimization/traffic-details',
    '/optimization/tag-manager',
  ];
  const hasHeaderSubBar = subBarRoots.some((root) => pathname.startsWith(root));

  // Header sub-bar is composed of:
  // - Tabs row: 52px
  // - Optional secondary toolbar row: 52px (see header.tsx condition)
  const secondaryToolbarHiddenPaths = new Set([
    '/optimization/activations/overview',
    '/brand-overview',
    '/admin/features/live-bidding/ad-group-based',
    '/brand-wayback',
    '/admin/users',
    '/admin/users/add',
    '/optimization/campaigns',
    '/optimization/scraping',
    '/optimization/activations/ko-bidding/target-impression-share',
    '/optimization/activations/ko-bidding/tcpa',
    '/optimization/activations/ko-bidding/troas',
    '/optimization/activations/ko-bidding/max-cpc',
    '/optimization/activations/active-mode',
    '/optimization/activations/ko-bidding-auto',
  ]);

  const hasHeaderSecondaryToolbar =
    hasHeaderSubBar &&
    !secondaryToolbarHiddenPaths.has(pathname) &&
    !pathname.startsWith('/workspaces/');

  const headerSubBarHeight = hasHeaderSubBar ? 1 : 0;
  const headerSecondaryToolbarHeight = hasHeaderSecondaryToolbar ? 1 : 0;
  const extraTop = headerSubBarHeight + headerSecondaryToolbarHeight + (hasHeaderSubBar ? SPACING : 0);

  if (isNavHorizontal) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: `${HEADER.H_MOBILE + 24 + extraTop}px`,
          pb: 10,
          ...(lgUp && {
            pt: `${HEADER.H_MOBILE * 2 + 40 + extraTop}px`,
            pb: 15,
          }),
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        minHeight: 1,
        display: 'flex',
        pt: hasHeaderSubBar ? `${HEADER.H_MOBILE + extraTop}px` : undefined,
        flexDirection: 'column',
        ...(lgUp && {
          width: `calc(100% - ${NAV.W_VERTICAL}px)`,
          pt: hasHeaderSubBar ? `${HEADER.H_DESKTOP + extraTop}px` : undefined,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI}px)`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  );
}
